// CommentStorageProvider.ts
import {
  Comment,
  CommentReply,
  CommentStatus,
  CommentAuditEntry,
  CreateCommentData,
  CreateReplyData,
} from "types/comments.types"

/**
 * Abstract interface for comment storage.
 * Allows swapping between localStorage, Yjs, or API backends.
 */
export interface CommentStorageProvider {
  // Lifecycle
  /**
   * Initialize the provider for a specific project
   */
  initialize(projectId: string, variantName?: string, owner?: string): Promise<void>

  /**
   * Clean up resources when done
   */
  dispose(): void

  /**
   * Check if provider is connected/ready
   */
  isReady(): boolean

  // CRUD Operations
  /**
   * Get all comments (including deleted if includeDeleted is true)
   */
  getAll(includeDeleted?: boolean): Comment[]

  /**
   * Get a specific comment by ID
   */
  get(commentId: string): Comment | null

  /**
   * Get comments filtered by variant
   */
  getByVariant(variantName: string, includeDeleted?: boolean): Comment[]

  /**
   * Add a new comment
   * @returns The ID of the created comment
   */
  add(comment: CreateCommentData): string

  /**
   * Update an existing comment
   */
  update(commentId: string, updates: Partial<Comment>): void

  /**
   * Mark a comment as deleted (soft delete for audit)
   */
  delete(
    commentId: string,
    deletedBy: string,
    deletedByName: string,
    reason: Comment["deletionReason"]
  ): void

  // Reply Operations
  /**
   * Add a reply to a comment thread
   */
  addReply(commentId: string, reply: CreateReplyData): string

  /**
   * Delete a reply from a comment thread
   */
  deleteReply(commentId: string, replyId: string): void

  // Status Operations
  /**
   * Change the status of a comment
   */
  setStatus(
    commentId: string,
    status: CommentStatus,
    userId: string,
    userName: string
  ): void

  /**
   * Resolve a comment
   */
  resolve(commentId: string, resolvedBy: string, resolvedByName: string): void

  // Audit Log
  /**
   * Get audit log entries, optionally filtered by comment ID
   */
  getAuditLog(commentId?: string): CommentAuditEntry[]

  // Events
  /**
   * Subscribe to comment changes (for real-time updates)
   * @returns Unsubscribe function
   */
  onCommentsChanged(callback: (comments: Comment[]) => void): () => void

  /**
   * Subscribe to connection status changes
   * @returns Unsubscribe function
   */
  onConnectionStatusChanged?(callback: (isConnected: boolean) => void): () => void
}

/**
 * Base class with common functionality for providers
 */
export abstract class BaseCommentStorageProvider
  implements CommentStorageProvider
{
  protected projectId: string = ""
  protected variantName: string = ""
  protected owner: string = ""
  protected comments: Map<string, Comment> = new Map()
  protected auditLog: CommentAuditEntry[] = []
  protected listeners: Set<(comments: Comment[]) => void> = new Set()
  protected connectionListeners: Set<(isConnected: boolean) => void> =
    new Set()
  protected ready: boolean = false

  abstract initialize(projectId: string, variantName?: string, owner?: string): Promise<void>
  abstract dispose(): void

  isReady(): boolean {
    return this.ready
  }

  getAll(includeDeleted: boolean = false): Comment[] {
    const all = Array.from(this.comments.values())
    return includeDeleted ? all : all.filter((c) => !c.isDeleted)
  }

  get(commentId: string): Comment | null {
    return this.comments.get(commentId) || null
  }

  getByVariant(variantName: string, includeDeleted: boolean = false): Comment[] {
    return this.getAll(includeDeleted).filter(
      (c) => c.variantName === variantName
    )
  }

  add(commentData: CreateCommentData): string {
    const id = crypto.randomUUID()
    const comment: Comment = {
      ...commentData,
      id,
      createdAt: new Date().toISOString(),
      replies: [],
      isDeleted: false,
    }

    this.comments.set(id, comment)
    this.addAuditEntry({
      commentId: id,
      action: "created",
      performedBy: commentData.authorId,
      performedByName: commentData.authorName,
      newValue: commentData.content,
    })
    this.notifyListeners()
    this.persist()

    return id
  }

  update(commentId: string, updates: Partial<Comment>): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    const previousContent = comment.content
    const updatedComment: Comment = {
      ...comment,
      ...updates,
      updatedAt: new Date().toISOString(),
    }

    this.comments.set(commentId, updatedComment)

    if (updates.content && updates.content !== previousContent) {
      this.addAuditEntry({
        commentId,
        action: "updated",
        performedBy: updatedComment.authorId,
        performedByName: updatedComment.authorName,
        previousValue: previousContent,
        newValue: updates.content,
      })
    }

    this.notifyListeners()
    this.persist()
  }

  delete(
    commentId: string,
    deletedBy: string,
    deletedByName: string,
    reason: Comment["deletionReason"]
  ): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    const updatedComment: Comment = {
      ...comment,
      isDeleted: true,
      deletedAt: new Date().toISOString(),
      deletedBy,
      deletedByName,
      deletionReason: reason,
    }

    this.comments.set(commentId, updatedComment)
    this.addAuditEntry({
      commentId,
      action: reason === "text_deleted" ? "text_deleted" : "deleted",
      performedBy: deletedBy,
      performedByName: deletedByName,
      metadata: { reason },
    })
    this.notifyListeners()
    this.persist()
  }

  addReply(commentId: string, replyData: CreateReplyData): string {
    const comment = this.comments.get(commentId)
    if (!comment) return ""

    const replyId = crypto.randomUUID()
    const reply: CommentReply = {
      ...replyData,
      id: replyId,
      createdAt: new Date().toISOString(),
    }

    const updatedComment: Comment = {
      ...comment,
      replies: [...comment.replies, reply],
      updatedAt: new Date().toISOString(),
    }

    this.comments.set(commentId, updatedComment)
    this.addAuditEntry({
      commentId,
      action: "replied",
      performedBy: replyData.authorId,
      performedByName: replyData.authorName,
      newValue: replyData.content,
    })
    this.notifyListeners()
    this.persist()

    return replyId
  }

  deleteReply(commentId: string, replyId: string): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    const updatedComment: Comment = {
      ...comment,
      replies: comment.replies.filter((r) => r.id !== replyId),
      updatedAt: new Date().toISOString(),
    }

    this.comments.set(commentId, updatedComment)
    this.notifyListeners()
    this.persist()
  }

  setStatus(
    commentId: string,
    status: CommentStatus,
    userId: string,
    userName: string
  ): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    const previousStatus = comment.status
    const updatedComment: Comment = {
      ...comment,
      status,
      updatedAt: new Date().toISOString(),
    }

    this.comments.set(commentId, updatedComment)
    this.addAuditEntry({
      commentId,
      action: "status_changed",
      performedBy: userId,
      performedByName: userName,
      previousValue: previousStatus,
      newValue: status,
    })
    this.notifyListeners()
    this.persist()
  }

  resolve(
    commentId: string,
    resolvedBy: string,
    resolvedByName: string
  ): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    const updatedComment: Comment = {
      ...comment,
      status: "resolved",
      resolvedAt: new Date().toISOString(),
      resolvedBy,
      resolvedByName,
      updatedAt: new Date().toISOString(),
    }

    this.comments.set(commentId, updatedComment)
    this.addAuditEntry({
      commentId,
      action: "resolved",
      performedBy: resolvedBy,
      performedByName: resolvedByName,
    })
    this.notifyListeners()
    this.persist()
  }

  getAuditLog(commentId?: string): CommentAuditEntry[] {
    if (commentId) {
      return this.auditLog.filter((entry) => entry.commentId === commentId)
    }
    return [...this.auditLog]
  }

  onCommentsChanged(callback: (comments: Comment[]) => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  onConnectionStatusChanged(
    callback: (isConnected: boolean) => void
  ): () => void {
    this.connectionListeners.add(callback)
    return () => this.connectionListeners.delete(callback)
  }

  protected notifyListeners(): void {
    const comments = this.getAll()
    this.listeners.forEach((cb) => cb(comments))
  }

  protected notifyConnectionListeners(isConnected: boolean): void {
    this.connectionListeners.forEach((cb) => cb(isConnected))
  }

  protected addAuditEntry(
    entry: Omit<CommentAuditEntry, "id" | "performedAt">
  ): void {
    this.auditLog.push({
      ...entry,
      id: crypto.randomUUID(),
      performedAt: new Date().toISOString(),
    })
  }

  /**
   * Persist data - implemented by subclasses
   */
  protected abstract persist(): void
}
